import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface AddGroupesElectrogenesProps {
  t: any;
  isRTL: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface GroupesElectrogenesProduct {
  name: string;
  mainImage: string;
  provider: string;
  description: string[];
  category: string;
  moteur?: string;
  alternateur?: string;
  capotage?: string;
  coffretDeCommande?: string;
  type?: string;
  capaciteReservoir?: string;
  autonomie75Charge?: string;
  puissancePRP?: string;
  puissanceLTP?: string;
  amperage?: string;
  regime?: string;
  frequence?: string;
  tension?: string;
  facteurPuissance?: string;
  charge25?: string;
  charge50?: string;
  charge75?: string;
  charge100?: string;
  prpPrimeRunningPower?: string;
  ltpLimitedTimePower?: string;
  refroidissement?: string;
  carburant?: string;
  longueur?: string;
  largeur?: string;
  hauteur?: string;
  poids?: string;
  additionalImages: string[];
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  
  &:hover {
    color: #dc2626;
  }
`;

const Title = styled.h2<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label<{ $isRTL: boolean }>`
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const Select = styled.select<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FileUploadLabel = styled.label<{ $isRTL: boolean }>`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  font-weight: 600;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  input[type="file"] {
    display: none;
  }
`;

const ImagePreview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const PreviewImage = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dc2626;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  
  &:hover {
    background: #b91c1c;
  }
`;

const UploadProgress = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin: 1rem 0;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const UploadStatus = styled.p`
  text-align: center;
  color: #6b7280;
  margin: 0.5rem 0;
  font-size: 0.9rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  font-size: 1.1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const AddGroupesElectrogenes: React.FC<AddGroupesElectrogenesProps> = ({ t, isRTL, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState<GroupesElectrogenesProduct>({
    name: '',
    mainImage: '',
    provider: '',
    description: [''],
    category: '1', // Groupes Electrogènes
    moteur: '',
    alternateur: '',
    capotage: '',
    coffretDeCommande: '',
    type: '',
    capaciteReservoir: '',
    autonomie75Charge: '',
    puissancePRP: '',
    puissanceLTP: '',
    amperage: '',
    regime: '',
    frequence: '',
    tension: '',
    facteurPuissance: '',
    charge25: '',
    charge50: '',
    charge75: '',
    charge100: '',
    prpPrimeRunningPower: '',
    ltpLimitedTimePower: '',
    refroidissement: '',
    carburant: '',
    longueur: '',
    largeur: '',
    hauteur: '',
    poids: '',
    additionalImages: []
  });

  // File upload states
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [mainImagePreview, setMainImagePreview] = useState<string>('');
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);

  const partners = [
    { value: 'nexans', label: 'Nexans' },
    { value: 'deutz', label: 'DEUTZ' },
    { value: 'ines', label: 'Ines Max' },
    { value: 'cahors', label: 'CAHORS' },
    { value: 'ifamel', label: 'IFAMEL' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setUploading(true);
      setUploadProgress(0);
      
      let mainImageUrl = formData.mainImage;
      let additionalImageUrls: string[] = [];

      // Upload main image if file is selected
      if (mainImageFile) {
        const mainImagePath = `products/${Date.now()}_main_${mainImageFile.name}`;
        mainImageUrl = await FirebaseService.uploadFile(mainImagePath, mainImageFile);
        setUploadProgress(25);
      }

      // Upload additional images if files are selected
      if (additionalImageFiles.length > 0) {
        const uploadPromises = additionalImageFiles.map(async (file, index) => {
          const imagePath = `products/${Date.now()}_additional_${index}_${file.name}`;
          return await FirebaseService.uploadFile(imagePath, file);
        });
        
        additionalImageUrls = await Promise.all(uploadPromises);
        setUploadProgress(75);
      }

      // Filter out empty description bullets
      const filteredDescription = formData.description.filter(bullet => bullet.trim() !== '');
      
      const productData = {
        ...formData,
        mainImage: mainImageUrl,
        additionalImages: additionalImageUrls,
        description: filteredDescription
      };

      setUploadProgress(90);
      await FirebaseService.create('products', productData);
      setUploadProgress(100);
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
    } finally {
      setLoading(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAdditionalImageFiles(prev => [...prev, ...files]);
      
      // Create previews for new files
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAdditionalImagePreviews(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (field: keyof GroupesElectrogenesProduct, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDescriptionChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      description: prev.description.map((desc, i) => i === index ? value : desc)
    }));
  };

  const addDescriptionBullet = () => {
    setFormData(prev => ({
      ...prev,
      description: [...prev.description, '']
    }));
  };

  const removeDescriptionBullet = (index: number) => {
    setFormData(prev => ({
      ...prev,
      description: prev.description.filter((_, i) => i !== index)
    }));
  };

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>×</CloseButton>
          
          <Title $isRTL={isRTL}>Add Groupes Electrogènes</Title>
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label $isRTL={isRTL}>Product Name</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Main Image</Label>
              <FileUploadContainer>
                <FileUploadLabel $isRTL={isRTL}>
                  {mainImageFile ? 'Change Main Image' : 'Upload Main Image'}
                  <input
                    type="file"
                    accept="image/*"
                    name="mainImage"
                    onChange={handleMainImageChange}
                    required={!formData.mainImage}
                  />
                </FileUploadLabel>
                {mainImagePreview && (
                  <ImagePreview>
                    <PreviewImage>
                      <img src={mainImagePreview} alt="Main preview" />
                    </PreviewImage>
                  </ImagePreview>
                )}
              </FileUploadContainer>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Additional Images</Label>
              <FileUploadContainer>
                <FileUploadLabel $isRTL={isRTL}>
                  Upload Additional Images
                  <input
                    type="file"
                    accept="image/*"
                    name="additionalImages"
                    multiple
                    onChange={handleAdditionalImagesChange}
                  />
                </FileUploadLabel>
                {additionalImagePreviews.length > 0 && (
                  <ImagePreview>
                    {additionalImagePreviews.map((preview, index) => (
                      <PreviewImage key={index}>
                        <img src={preview} alt={`Additional ${index + 1}`} />
                        <RemoveImageButton onClick={() => removeAdditionalImage(index)}>
                          ×
                        </RemoveImageButton>
                      </PreviewImage>
                    ))}
                  </ImagePreview>
                )}
              </FileUploadContainer>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Provider</Label>
              <Select
                $isRTL={isRTL}
                value={formData.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                required
              >
                <option value="">Select Provider</option>
                {partners.map(partner => (
                  <option key={partner.value} value={partner.value}>
                    {partner.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Description</Label>
              {formData.description.map((desc, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={desc}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder={`Description point ${index + 1}`}
                  />
                  {formData.description.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDescriptionBullet(index)}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addDescriptionBullet}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                + Add Description Point
              </button>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Technical Specifications</Label>
              <SpecsGrid>
                <FormGroup>
                  <Label $isRTL={isRTL}>Moteur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.moteur || ''}
                    onChange={(e) => handleInputChange('moteur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Alternateur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.alternateur || ''}
                    onChange={(e) => handleInputChange('alternateur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Capotage</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.capotage || ''}
                    onChange={(e) => handleInputChange('capotage', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Coffret de commande</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.coffretDeCommande || ''}
                    onChange={(e) => handleInputChange('coffretDeCommande', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Type</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.type || ''}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Capacité du réservoir</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.capaciteReservoir || ''}
                    onChange={(e) => handleInputChange('capaciteReservoir', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Autonomie à 75% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.autonomie75Charge || ''}
                    onChange={(e) => handleInputChange('autonomie75Charge', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Puissance (PRP)</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.puissancePRP || ''}
                    onChange={(e) => handleInputChange('puissancePRP', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>PRP (Prime Running Power)</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.prpPrimeRunningPower || ''}
                    onChange={(e) => handleInputChange('prpPrimeRunningPower', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Puissance (LTP)</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.puissanceLTP || ''}
                    onChange={(e) => handleInputChange('puissanceLTP', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>LTP (Limited Time Power)</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.ltpLimitedTimePower || ''}
                    onChange={(e) => handleInputChange('ltpLimitedTimePower', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Ampérage</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.amperage || ''}
                    onChange={(e) => handleInputChange('amperage', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Régime</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.regime || ''}
                    onChange={(e) => handleInputChange('regime', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Fréquence</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.frequence || ''}
                    onChange={(e) => handleInputChange('frequence', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Tension</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.tension || ''}
                    onChange={(e) => handleInputChange('tension', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Facteur de Puissance</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.facteurPuissance || ''}
                    onChange={(e) => handleInputChange('facteurPuissance', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>25% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.charge25 || ''}
                    onChange={(e) => handleInputChange('charge25', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>50% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.charge50 || ''}
                    onChange={(e) => handleInputChange('charge50', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>75% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.charge75 || ''}
                    onChange={(e) => handleInputChange('charge75', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>100% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.charge100 || ''}
                    onChange={(e) => handleInputChange('charge100', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label $isRTL={isRTL}>Refroidissement</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.refroidissement || ''}
                    onChange={(e) => handleInputChange('refroidissement', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Carburant</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.carburant || ''}
                    onChange={(e) => handleInputChange('carburant', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Longueur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.longueur || ''}
                    onChange={(e) => handleInputChange('longueur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Largeur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.largeur || ''}
                    onChange={(e) => handleInputChange('largeur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Hauteur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.hauteur || ''}
                    onChange={(e) => handleInputChange('hauteur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Poids</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.poids || ''}
                    onChange={(e) => handleInputChange('poids', e.target.value)}
                  />
                </FormGroup>
              </SpecsGrid>
            </FormGroup>

            {uploading && (
              <>
                <UploadProgress>
                  <ProgressBar $progress={uploadProgress} />
                </UploadProgress>
                <UploadStatus>
                  {uploadProgress < 25 && 'Preparing upload...'}
                  {uploadProgress >= 25 && uploadProgress < 75 && 'Uploading images...'}
                  {uploadProgress >= 75 && uploadProgress < 90 && 'Processing images...'}
                  {uploadProgress >= 90 && 'Creating product...'}
                </UploadStatus>
              </>
            )}

            <SubmitButton type="submit" disabled={loading || uploading}>
              {loading ? 'Creating...' : uploading ? 'Uploading...' : 'Create Groupes Electrogènes'}
            </SubmitButton>
          </Form>
        </Modal>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default AddGroupesElectrogenes; 